// ProjectBoardCard — one board card's innards (the shell, click handling and
// column grouping belong to DataBoard). Mirrors the table's cells with the
// same meta helpers: icon tile + name/badge + owner avatar, priority chip +
// end date with the OVERDUE rule, tag chips, and a members footer — the
// avatar stack alone (dash when empty), defaults included like any other
// project; a task-progress row still waits on backend fields.

import { useTranslation } from 'react-i18next';
import { Avatar, AvatarStack, Chip, Icon } from '@/components/common';
import type { ProjectListItem } from '@/api/endpoints/projectsApi';
import toneOf from '@/utils/avatarTone';
import { PRIORITY_META } from './projectColumns';
import { fmtDate } from '@/utils/formatDate';
import initialsOf from '@/utils/initials';

interface ProjectBoardCardProps {
  project: ProjectListItem;
}

const ProjectBoardCard = ({ project }: ProjectBoardCardProps) => {
  const { t } = useTranslation();

  const priority = PRIORITY_META[project.priority];
  // Table parity: any non-archived project past its end date reads OVERDUE —
  // the operational state doesn't gate it.
  const overdue =
    project.status !== 'archived' &&
    !!project.endDate &&
    new Date(project.endDate).getTime() < Date.now();
  const shownTags = project.tags.slice(0, 2);
  const extraTags = project.tags.length - shownTags.length;

  return (
    <div className='project-board-card'>
      <div className='project-board-card__head'>
        <span
          className={`project-board-card__tile${
            project.isDefault ? ' project-board-card__tile--default' : ''
          }`}
        >
          <Icon name={project.icon} size={20} strokeWidth={2} />
        </span>
        <span className='project-board-card__name'>
          <span className='project-board-card__name-text'>{project.name}</span>
        </span>
        <Avatar
          initials={initialsOf(project.owner.name)}
          size={32}
          tone={toneOf(project.owner.name)}
        />
      </div>

      <div className='project-board-card__meta'>
        <Chip variant={priority.chip} iconStart={priority.icon}>
          {t(`projects_priority_${project.priority}`)}
        </Chip>
        <span className={`project-board-card__date${overdue ? ' is-overdue' : ''}`}>
          <Icon name={overdue ? 'calendar-x' : 'calendar'} size={14} />
          {fmtDate(project.endDate)}
          {overdue && <b className='project-board-card__overdue'>{t('projects_overdue')}</b>}
        </span>
      </div>

      <div className='project-board-card__tags'>
        {project.tags.length === 0 ? (
          <span className='project-board-card__dash'>—</span>
        ) : (
          <>
            {shownTags.map((tag) => (
              <Chip key={tag.id} variant='neutral'>
                {tag.title}
              </Chip>
            ))}
            {extraTags > 0 && <span className='project-board-card__more'>+{extraTags}</span>}
          </>
        )}
      </div>

      <span className='project-board-card__footer'>
        {project.members.length === 0 ? (
          <span className='project-board-card__dash'>—</span>
        ) : (
          <AvatarStack max={3} size={32}>
            {project.members.map((m) => (
              <Avatar key={m.id} initials={initialsOf(m.name)} size={32} tone={toneOf(m.name)} />
            ))}
          </AvatarStack>
        )}
      </span>
    </div>
  );
};

export default ProjectBoardCard;
